import { useEffect, useRef } from 'react';
import {
  analyticsEvents,
  AnalyticsServiceFeeLocation,
} from '../utils/analytics/analytics';
import { OrderType } from '../types';

type ServiceFeeViewParams = {
  // Whether the surrounding element that holds the fee row is rendered at all. The
  // callers gate the row on more than the amount (an open restaurant, loaded prices),
  // so they pass that condition in rather than duplicating it here.
  isRendered: boolean;
  location: AnalyticsServiceFeeLocation;
  serviceFee: number | null | undefined;
  orderType: OrderType | null;
  restaurantId?: number;
  cartValue?: number;
};

/**
 * Reports that the customer was shown a service fee, once per place it appears.
 *
 * Mounting is the impression: the fee sits in a summary the customer has to pass on
 * the way to checkout, and the funnel this feeds wants a stable denominator rather
 * than a scroll-depth-dependent one.
 *
 * The latch keys on the restaurant so a row reused for a different restaurant reports
 * again, while re-renders (price refreshes, campaign data settling) do not.
 */
export const useServiceFeeViewEvent = ({
  isRendered,
  location,
  serviceFee,
  orderType,
  restaurantId,
  cartValue,
}: ServiceFeeViewParams): void => {
  const reportedKey = useRef<string | null>(null);

  useEffect((): void => {
    if (!isRendered || !serviceFee || serviceFee <= 0) return;

    const key = `${location}:${restaurantId ?? ''}`;
    if (reportedKey.current === key) return;
    reportedKey.current = key;

    analyticsEvents.viewServiceFee({
      location,
      service_fee: serviceFee,
      order_type: orderType,
      restaurant_id: restaurantId,
      cart_value: cartValue,
    });
    // cartValue is read but intentionally not a dependency: it changes as the customer
    // edits the cart, and the impression has already been reported by then.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isRendered, location, serviceFee, orderType, restaurantId]);
};
